import React, { useState, useLayoutEffect } from "react"

import "./styles.scss"
import InsightsHeader from "../Insights/InsightsHeader"
import aboutBackgroundImage from "../images/aboutBackground.webp"

import slide0Image from "../images/slideshow/Slide_1_2026.webp"
import slide1Image from "../images/slideshow/21-OCT_Slide-1_02.webp"
import slide2Image from "../images/slideshow/21-OCT_Slide-1_03.jpg"
import slide3Image from "../images/slideshow/Slide_2_2026.webp"
import slide4Image from "../images/slideshow/21-OCT_Slide-1_05.webp"
import slide5Image from "../images/slideshow/21-OCT_Slide-1_06.jpg"

import secondSlide0Image from "../images/slideshow/21-OCT_Slide-2_01.webp"
import secondSlide1Image from "../images/slideshow/21-OCT_Slide-2_02.webp"
import secondSlide2Image from "../images/slideshow/21-OCT_Slide-2_03.webp"
import secondSlide3Image from "../images/slideshow/21-OCT_Slide-2_04.jpg"
import secondSlide4Image from "../images/slideshow/21-OCT_Slide-2_05.webp"
import secondSlide5Image from "../images/slideshow/21-OCT_Slide-2_06.webp"
import secondSlide6Image from "../images/slideshow/21-OCT_Slide-2_07.webp"

//images for Who Uses WJP
import whoUsesSlide0Image from "../images/slideshow/whoUsesSlide00.webp"
import whoUsesSlide1Image from "../images/slideshow/whoUsesSlide01.webp"
import whoUsesSlide2Image from "../images/slideshow/whoUsesSlide02.webp"
import whoUsesSlide3Image from "../images/slideshow/whoUsesSlide03.webp"
import whoUsesSlide4Image from "../images/slideshow/whoUsesSlide04.webp"
import whoUsesSlide5Image from "../images/slideshow/whoUsesSlide05.webp"

import DataLoader, { latestYear } from "../DataLoader"
import { group } from "d3-array"
import { Helmet } from "react-helmet"
import { Link } from "react-router-dom"
import classNames from "classnames"
const factorColors = {
  "Constraints on Government Powers": "#227B3A",
  "Absence of Corruption": "#6AAD50",
  "Open Government": "#2AAA9F",
  "Fundamental Rights": "#1E72BE",
  "Regulatory Enforcement": "#4B2795",
  "Criminal Justice": "#FF651F",
  "Civil Justice": "#A12224",
  "Order and Security": "#2A268F",
}
const slides1 = [
  {
    title: "The WJP Rule of Law Index at a glance",
    description: (
      <>
        Learn about the{" "}
        <i>
          WJP Rule of Law Index
          <sup>
            <small>®</small>
          </sup>
        </i>{" "}
        and how it measures human rights, access to justice, corruption and
        authoritarianism worldwide.
      </>
    ),
    image: slide0Image,
    // noStretch: true,
  },
  {
    title: "The foundation for opportunity and peace.",
    description: (
      <>
        A positive correlation has been found between rule of law and economic
        development, health, education, and security.
        <br />
        <br />
        Effective rule of law reduces corruption, combats poverty and disease,
        and protects people from injustices large and small. It is the
        foundation for communities of justice, equality, opportunity, and
        peace—underpinning development, accountability, democracy, and respect
        for fundamental rights.
      </>
    ),
    image: slide1Image,
  },
  {
    title: "To improve the rule of law, we first need to measure it. ",
    longestTitle: true,
    longestDescription: true,
    description: (
      <>
        The{" "}
        <i>
          WJP Rule of Law Index
          <sup>
            <small>®</small>
          </sup>
        </i>{" "}
        provides citizens, governments, policymakers, donors, businesses, media,
        academics, and civil society organizations with a uniquely comparative
        analysis of how well countries adhere to universal rule of law
        principles.
        <br />
        <br />
        By offering detailed information on an annual basis, the Index has
        become an indispensable tool to identify areas for improvement, develop
        policies for positive social change, and track their subsequent
        progress.
      </>
    ),
    image: slide2Image,
  },
  {
    title: "Collecting original data since 2008.",
    description: (
      <>
        First piloted in Argentina, Australia, Colombia, Spain, Sweden and the
        United States, the <i>WJP Rule of Law Index</i> now includes 143
        countries and jurisdictions.
      </>
    ),
    image: slide3Image,
    // noStretch: true,
  },
  {
    title: "Everyone is a stakeholder.",
    description: (
      <>
        Traditionally, the rule of law has been viewed as the domain of lawyers
        and judges. But everyday issues of safety, rights, access to justice,
        and governance affect us all.
        <br />
        <br />
        This is why the Index relies on first-hand accounts from more than
        215,000 households and 3,400 experts to measure how the rule of law is
        experienced and perceived in practical, everyday situations.
      </>
    ),
    image: slide4Image,
  },
  {
    title: "A comprehensive approach.",
    description: (
      <>
        The Index is the world’s most comprehensive dataset of its kind and the
        only one to rely principally on primary data.
        <br />
        <br />
        It evaluates and ranks countries across key rule of law indicators,
        including checks on government powers, corruption and transparency,
        fundamental and equal rights, open government, civic engagement,
        security, business regulation, and the functioning of regulatory,
        criminal, and civil justice systems.
      </>
    ),
    image: slide5Image,
  },
]

const slides2 = [
  {
    title: "How we measure",
    description: (
      <>
        The{" "}
        <i>
          WJP Rule of Law Index
          <sup>
            <small>®</small>
          </sup>
        </i>{" "}
        is built upon an original conceptual framework developed by our
        researchers, in consultation with academics, practitioners, and
        community leaders around the world.
        <br />
        <br />
        Understand our method and evaluation process.
      </>
    ),
    image: secondSlide0Image,
  },

  {
    title: "Questionnaires",
    description:
      "A set of five questionnaires is administered to experts and the general public. These are translated into several languages and adapted to reflect commonly used terms and expressions.",
    image: secondSlide1Image,
  },
  {
    title: "Codification",
    description: (
      <>
        WJP collects and maps the data onto 44 rule of law indicators. Scores
        for each of these sub-factors are globally comparable.
        <br />
        <br />
        Final scores are constructed using a five-step process.
      </>
    ),
    image: secondSlide2Image,
  },
  {
    title: "Testing",
    description:
      "The data is subjected to a series of tests to identify possible biases and errors. For example, all sub-factors are cross-checked against more than 60 third-party sources, including quantitative data and qualitative assessments drawn from local and international organizations.",
    image: secondSlide3Image,
  },
  {
    title: "External Review",
    longestTitle: true,
    description:
      " To assess the statistical reliability of our results, we invited the Econometrics and Applied Statistics Unit of the European Commission’s Joint Research Centre to perform a sensitivity analysis.",
    image: secondSlide4Image,
  },
  {
    title: "Proofing",
    description:
      "To illustrate whether the rule of law in a country has significantly changed over the course of the past year, a measure of change over time is produced based on the annual difference in the country-level factor scores, the standard errors of these scores (estimated from a set of 100 bootstrap samples), and the results of the corresponding t-tests.",
    longestDescription: true,
    image: secondSlide5Image,
  },
  {
    title: "Presentation",
    description:
      "Data is organized into country profiles, tables, and figures to facilitate their presentation and interpretation. You can access the full report, country profiles, and historical datasets in the download button on the top right.",
    image: secondSlide6Image,
  },
]

//Who uses WJP Index slideshow
const slidesWhoUsesIndex = [
  {
    title: "Who uses the WJP Rule of Law Index",
    description: (
      <>
        As the world's leading source of original, independent rule of law data,
        the{" "}
        <i>
          WJP Rule of Law Index
          <sup>
            <small>®</small>
          </sup>
        </i>{" "}
        is an invaluable tool for advancing rule of law reforms, global
        development, and business investment around the globe.
        <br />
        <br />
        Find out who uses our data.
      </>
    ),
    image: whoUsesSlide0Image,
  },

  {
    title: "Helping governments track progress",
    description: (
      <>
        Governments around the world use the{" "}
        <i>
          WJP Rule of Law Index<sup>®</sup>
        </i>{" "}
        to benchmark and track their progress on critical rule of law reform
        efforts. The Index helps national institutions and policymakers advance
        justice, human rights, open government, and anti-corruption
        strategies—and monitor global goals, including Sustainable Development
        Goal 16.
        <br />
        <br />
        Governments that use our Index include the European Union, Honduras,
        India, Kazakhstan, Kosovo, Mexico, Philippines, Thailand, United States,
        and Uzbekistan.
      </>
    ),
    image: whoUsesSlide1Image,
  },
  {
    title: "Trustworthy data for policy development",
    description:
      "Major international organizations use the Index to inform their global development policies and regional investment strategies. The World Bank, OECD, the IMF, the U.S. State Department, the European Commission, the Millennium Challenge Corporation, and the United Nations Development Programme are among those that leverage WJP data.",
    image: whoUsesSlide2Image,
  },
  {
    title: "Inspiring change big or small",
    longestTitle: true,
    description: (
      <>
        Civil society organizations big and small turn to the Index data to
        identify weaknesses, advocate for change, and monitor rule of law
        performance across 44 sub-factors. WJP's original, independent data
        helps shape Transparency International's{" "}
        <i>Corruption Perceptions Index</i>, the Chandler Institute of
        Governance's <i>Chandler Good Government Index</i>, the World Bank's{" "}
        <i>Worldwide Governance Indicators</i> (WGI), and the Mo Ibrahim
        Foundation's <i>Ibrahim Index of African Governance</i>.
      </>
    ),
    image: whoUsesSlide3Image,
  },
  {
    title: "Evaluating risk and business sustainability",
    description: (
      <>
        Gathering and analyzing original rule of law data since 2008, the{" "}
        <i>
          WJP Rule of Law Index<sup>®</sup>
        </i>{" "}
        offers objective business climate benchmarks over time, in 143 countries
        and jurisdictions.
        <br />
        <br />
        Private sector users such as Moody's, Standard and Poor's, Microsoft,
        UBS, JP Morgan, Hewlett-Packard Enterprise, Eurasia Group, LexisNexis,
        and the U.S. Chamber of Commerce leverage the Index to evaluate
        investment opportunities, global supply chains, business sustainability,
        legal and political risk, democracy, human rights, and corruption.
      </>
    ),
    longestDescription: true,
    image: whoUsesSlide4Image,
  },
  {
    title: "Informing research and reporting",
    description:
      "The Index's rigorous methodology and broad reach make it a reliable source for researchers and journalists around the world.  Index data has been cited in more than 2,000 academic papers and by media outlets in over 190 countries, elevating understanding of critical rule of law issues and holding leaders to account. In the last two years, the Index was featured in more than 9,000 news articles around the world.",
    image: whoUsesSlide5Image,
  },
]

const pastLinks = {
  2025: "https://worldjusticeproject.org/sites/default/files/documents/WJPIndex2025.pdf",
  2024: "https://worldjusticeproject.org/sites/default/files/documents/WJPIndex2024.pdf",
  2023: "https://worldjusticeproject.org/sites/default/files/documents/WJPIndex2023.pdf",
  2022: "https://worldjusticeproject.org/sites/default/files/documents/WJPIndex2022.pdf",
  2021: "https://worldjusticeproject.org/sites/default/files/documents/Index-2021.pdf",
  2020: "https://worldjusticeproject.org/sites/default/files/documents/Index-2020-English.pdf",
  2019: "https://worldjusticeproject.org/sites/default/files/documents/ROLI-2019-Reduced.pdf",
  "2017-18":
    "https://worldjusticeproject.org/sites/default/files/documents/WJP-ROLI-2018-June-Online-Edition_0.pdf",
  2016: "https://worldjusticeproject.org/sites/default/files/documents/RoLI_Final-Digital_0.pdf",
  2015: "https://worldjusticeproject.org/sites/default/files/documents/roli_2015_0.pdf",
  2014: "https://worldjusticeproject.org/sites/default/files/documents/RuleofLawIndex2014.pdf",

  "2012-13":
    "https://worldjusticeproject.org/sites/default/files/documents/WJP_Index_Report_2012.pdf",
  2011: "https://worldjusticeproject.org/sites/default/files/documents/WJP_Rule_of_Law_Index_2011_Report.pdf",
  2010: "https://worldjusticeproject.org/sites/default/files/documents/WJP_Rule_of_Law_Index_2010_Report.pdf",
  2009: "https://worldjusticeproject.org/sites/default/files/documents/WJP_Rule_of_Law_Index_2009_Report.pdf",
  2008: "https://worldjusticeproject.org/sites/default/files/documents/WJP_Rule_of_Law_Index_2008_Report_.pdf",
}
const nonCoveredYears = ["2008", "2009", "2010", "2011", "2012-13", "2014"]

const Next = (props) => {
  const style = {}
  if (props.disabled) {
    style.opacity = 0.2
    style.pointerEvents = "none"
  }
  return (
    <svg
      style={style}
      onMouseDown={props.onClick}
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
    >
      <path
        id="Path_29856"
        data-name="Path 29856"
        d="M22,16,12,26l-1.4-1.4L19.2,16,10.6,7.4,12,6Z"
      />
      <rect
        id="_x3C_Transparent_Rectangle_x3E_"
        width="32"
        height="32"
        fill="none"
      />
    </svg>
  )
}

const Previous = (props) => {
  return (
    <svg
      onMouseDown={props.onClick}
      xmlns="http://www.w3.org/2000/svg"
      width="32"
      height="32"
      viewBox="0 0 32 32"
    >
      <path d="M10,16,20,6l1.4,1.4L12.8,16,21.4,24.6,20,26Z" />
      <rect
        id="_x3C_Transparent_Rectangle_x3E_"
        width="32"
        height="32"
        fill="none"
      />
    </svg>
  )
}

function Arrow(props) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <path
        d="M19.14,13.491l-7.491,7.491L10.6,19.934l6.443-6.443L10.6,7.049,11.649,6Z"
        transform="translate(-2.659 -1.505)"
        fill="#fff"
      />
    </svg>
  )
}

function Slideshow(props) {
  const { slides, navlink } = props
  const [currentSlide, setCurrentSlide] = useState(0)
  const nextSlide = (e) => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1)
    }
    e.preventDefault()
    e.stopPropagation()
  }
  const showStartButton = currentSlide === 0
  const fontSize = 16

  const maxContentWidth = Math.min(
    window.innerWidth - fontSize * 2,
    fontSize * 58,
  )
  const buttons = showStartButton ? (
    <button className="important" onClick={nextSlide}>
      View Slideshow <Arrow />
    </button>
  ) : (
    <div className="prevNext">
      <Previous
        onClick={(e) => {
          setCurrentSlide(currentSlide - 1)

          e.preventDefault()
          e.stopPropagation()
        }}
      />
      <Next disabled={currentSlide === slides.length - 1} onClick={nextSlide} />
    </div>
  )
  const imageSize = Math.round(
    (window.innerWidth < 900 ? 1 : window.innerWidth > 1090 ? 0.68 : 0.65) *
      maxContentWidth,
  )
  const imageContainerSize = slides.length * imageSize
  const activeSlide = slides[currentSlide]
  const translation = `translateX(-${currentSlide * imageSize}px)`
  const progress = showStartButton ? null : (
    <div className="progress">
      <div className="progressText">
        {`${currentSlide}`.padStart(2, "0")} /{" "}
        {`${slides.length - 1}`.padStart(2, "0")}
      </div>
      <div className="progressBar" />
    </div>
  )
  const slideContentStyle = {
    // height: props.height,
  }
  const longestDescription = slides.find(
    (d) => d.longestDescription,
  ).description
  const longestTitle = slides.find((d) => d.longestTitle).title
  return (
    <>
      {navlink ? <span className="navLink" id={navlink} /> : null}
      <div className="Slideshow">
        <div className="slideContent" style={slideContentStyle}>
          {progress}
          <div className="slideTitle aboutTitle multiline">
            <div>{activeSlide.title}</div>
            <div className="hidden">{longestTitle}</div>
          </div>
          <div className="slideDescription multiline">
            <div>{activeSlide.description}</div>
            <div className="hidden">{longestDescription}</div>
          </div>
          {buttons}
        </div>
        <div className="slideWindow" style={{ width: imageSize }}>
          {/* <div className='slideWindow' style={{ width: '50%' }}> */}
          <div
            className="slides"
            style={{ width: imageContainerSize, transform: translation }}
          >
            {/* <div className='slides' style={{ width: '100%' }}> */}
            {slides.map((slide, index) => {
              return (
                <img
                  className={classNames({ noStretch: slide.noStretch })}
                  loading="lazy"
                  style={{
                    width: imageSize,
                    padding: slide.portrait ? "0 6em" : null,
                  }}
                  key={index}
                  src={slide.image}
                  alt={slide.title}
                />
                // <img loading="lazy" style={{  padding: slide.portrait ? '0 6em' : null}}  key={index} src={slide.image} alt={slide.title} />
              )
            })}
          </div>
        </div>
        {/* <div className='slideControls'>
          {slides.map((slide, index) => {
            return (
              <div className={classnames('slideControl', { active: currentSlide === index })} key={index} onClick={() => setCurrentSlide(index)} />
            )
          })}
        </div> */}
      </div>
    </>
  )
}

export default function About(props) {
  const years = [...Object.keys(DataLoader.dataByYear), ...nonCoveredYears]
  years.sort().reverse()
  const maxYear = Math.max(
    ...years.map((year) => +year).filter((d) => !isNaN(d)),
  )
  const factors = DataLoader.dataByYear[maxYear].aggregateFactorKeys
  const subfactors = Array.from(
    group(DataLoader.dataByYear[maxYear].questionKeys, (d) => d[0]),
  )

  useLayoutEffect(() => {
    let scroller = () => {
      if (window.location.hash) {
        const element = document.querySelector(window.location.hash)
        if (element) {
          element.scrollIntoView({ behavior: "smooth" })
        }
      } else {
        window.scrollTo({ top: 0 })
      }
    }
    setTimeout(() => {
      scroller()
    }, 500)
  }, [])

  const logDownload = (label) => () => {
    if (window.gtag) {
      window.gtag("event", "download", {
        event_category: "download",
        event_label: label,
      })
    }
  }
  return (
    <div className="About">
      <Helmet>
        <title>WJP Rule of Law Index | About</title>
        <meta
          name="description"
          content={`Explore rule of law rankings for 143 countries in the WJP Rule of Law Index. The World Justice Project Rule of Law Index® ${latestYear} is the latest report in an annual series measuring how the rule of law is experienced and perceived based on global household and expert surveys. The ${latestYear} edition covers 143 countries and jurisdictions worldwide.`}
        />
      </Helmet>
      <InsightsHeader
        title={
          <span style={{ fontWeight: "bold" }}>
            The world’s leading source for{" "}
            <strong style={{ color: "#fff" }}>
              original, independent data
            </strong>{" "}
            on the rule of law.
          </span>
        }
        backgroundImage={aboutBackgroundImage}
        links={[
          { text: "The Index at a glance", url: "#ataglance" },
          { text: "What we measure", url: "#whatwemeasure" },
          { text: "How we measure", url: "#howwemeasure" },
          { text: "Who uses the Index", url: "#whousesindex" },
          { text: "Contact", url: "#contact" },
          { text: "Past reports", url: "#reports" },
        ]}
        color="#400099"
      />
      <div className="AboutContainer">
        <Slideshow height="25em" slides={slides1} navlink="ataglance" />
        <div style={{ margin: "4em 0" }}>
          <div className="blackText" style={{ marginTop: "3em" }}>
            For more information
          </div>
          <div className="buttons">
            <a
              className="button"
              target="_blank"
              rel="noopener noreferrer"
              href="https://worldjusticeproject.org/news/2026-wjp-rule-law-index-video"
            >
              Watch Video
            </a>
            <a
              className="button"
              target="_blank"
              rel="noopener noreferrer"
              href="https://worldjusticeproject.org/about-us/overview/what-rule-law"
            >
              See Our Rule of Law Definition
            </a>
            <a
              onClick={logDownload("Full Report")}
              className="button"
              target="_blank"
              rel="noopener noreferrer"
              href={`${process.env.PUBLIC_URL}/downloads/WJPIndex${latestYear}.pdf`}
            >
              Download Report
            </a>
            <a
              className="button"
              target="_blank"
              rel="noopener noreferrer"
              href="https://worldjusticeproject.org/news/wjp-rule-law-index-2026-global-press-release"
            >
              Read Press Release
            </a>
          </div>
        </div>
        <div className="aboutTitle">
          <span className="navLink" id="whatwemeasure" />
          What we measure
        </div>
        {/* <div className='blackText'>Lorem ipsum dolor sit amet, consectetur adipiscing elit. Praesent ac magna ultrices, ultrices urna eu, molestie quam. Aliquam pulvinar egestas cursus.</div> */}
        <div className="AboutFactors">
          {factors.map((factor, factorIndex) => {
            const factorName = factor.split(": ")[1]
            const factorSubfactors = subfactors.find(
              (group) => group[0] === `${factorIndex + 1}`,
            )
            const color = factorColors[factorName]
            return (
              <div key={factorIndex} className="Factor">
                <Link
                  className="factorName"
                  style={{ color, borderColor: color }}
                  to={`/factors/${maxYear}/${factorName}`}
                >
                  {factorName}
                </Link>

                <div className="subfactors">
                  {factorSubfactors[1].map((subfactor, subfactorIndex) => {
                    return (
                      <div className="subfactor" key={subfactorIndex}>
                        {subfactor.split(" ").slice(1).join(" ")}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
          <div className="Factor" style={{ order: 10 }}>
            <div
              className="factorName"
              style={{ color: "#400099", borderColor: "#400099" }}
            >
              Informal Justice*
            </div>
            <div className="subfactors">
              <div className="subfactor">
                Informal justice is timely and effective
              </div>
              <div className="subfactor">
                Informal justice is impartial and free of improper influence
              </div>
              <div className="subfactor">
                Informal justice respects and protects fundamental rights
              </div>
            </div>
          </div>
        </div>
        <div className="disclaimer">
          * Informal Justice is not included in the{" "}
          <i>
            WJP Rule of Law Index<sup>®</sup>
          </i>
          . While WJP has devoted significant effort to collecting data on
          informal justice in a dozen countries, the complexities of these
          systems and the difficulties of measuring their fairness and
          effectiveness in a manner that is both systematic and comparable
          across countries, make assessments extraordinarily challenging.
        </div>
        <Slideshow slides={slides2} navlink="howwemeasure" />
        <div className="blackText" style={{ marginTop: "4em" }}>
          For more information consult
        </div>
        <div className="buttons">
          <a
            onClick={logDownload("Methodology")}
            className="button"
            target="_blank"
            rel="noopener noreferrer"
            href={`${process.env.PUBLIC_URL}/downloads/Index-Methodology-${latestYear}.pdf`}
          >
            Full Methodology
          </a>
          <a
            className="button"
            target="_blank"
            rel="noopener noreferrer"
            href="https://papers.ssrn.com/sol3/papers.cfm?abstract_id=1966257"
          >
            Measuring The Rule of Law Paper
          </a>
          <a
            className="button"
            target="_blank"
            rel="noopener noreferrer"
            href="https://publications.jrc.ec.europa.eu/repository/handle/JRC131884"
          >
            Statistical Audit
          </a>
          <a
            onClick={logDownload("Variable Map")}
            className="button"
            target="_blank"
            rel="noopener noreferrer"
            href={`${process.env.PUBLIC_URL}/downloads/ROLIndex${latestYear}_Table_of_Variables.pdf`}
          >
            Variable Map
          </a>
          <a
            onClick={logDownload("Questionnaires")}
            className="button"
            target="_blank"
            rel="noopener noreferrer"
            href={`${process.env.PUBLIC_URL}/downloads/ROLIndex${latestYear}_Questionnaires.zip`}
          >
            Questionnaires
          </a>
        </div>
        <Slideshow slides={slidesWhoUsesIndex} navlink="whousesindex" />
        <br />
      </div>
      <div
        className="contact"
        style={{ backgroundImage: `url(${aboutBackgroundImage})` }}
      >
        <span id="contact" className="navLink" />
        <div className="AboutContainer">
          <div className="left">
            <div className="contactTitle aboutTitle">Contact</div>
            <div className="contactText">
              We understand that you might have questions regarding our data and
              methodology. If you are interested in using our data,
              collaborating, or want to speak with one of our experts, please
              feel free to reach out.{" "}
            </div>
          </div>
          <div className="right">
            <div className="contactItem">
              Media inquiries
              <a href="mailto:press@worldjusticeproject.org">
                press@worldjusticeproject.org
              </a>
            </div>
            <div className="contactItem">
              Research and collaboration inquiries
              <a href="mailto:wjp@worldjusticeproject.org">
                wjp@worldjusticeproject.org
              </a>
            </div>
          </div>
        </div>
      </div>
      <div className="AboutContainer">
        <div className="pastReports">
          <span className="navLink" id="reports" />
          <div className="aboutTitle">Past reports</div>
          {/* <a href="https://worldjusticeproject.org/our-work/research-and-data/wjp-rule-law-index-2021/previous-editions-wjp-rule-law-index">All that are available here</a> */}
          The{" "}
          <i>
            WJP Rule of Law Index
            <sup>
              <small>®</small>
            </sup>
          </i>{" "}
          was first piloted in 2008. Since then it has evolved, expanding to
          include 143 countries and jurisdictions. You can consult our previous
          editions here.
          <div className="reports">
            {years
              .filter((d) => +d !== +maxYear)
              .map((year) => {
                const yearLink = pastLinks[year]
                return (
                  <a
                    key={year}
                    href={yearLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      alt={`${year} report`}
                      src={`${process.env.PUBLIC_URL}/pastReports/pastReport${year}.webp`}
                      key={year}
                    />
                  </a>
                )
              })}
          </div>
        </div>
      </div>
    </div>
  )
}
